import React from "react";
import { PlayCircle, X } from "lucide-react";
import { useDemoMode } from "../context/DemoModeContext";
import { useAuth } from "../context/AuthContext";

/**
 * DemoModeBanner — sticky top banner that appears whenever demo mode
 * is active. Three jobs:
 *   1. Reassure the founder mid-demo that they're still in posture.
 *   2. Remind them this is a non-default state (TTS + mic forced on).
 *   3. Give a one-tap exit so they can flip back to regular UX.
 *
 * Renders globally (mounted in App), but only when demoActive AND
 * the user is admin — defence-in-depth against an accidentally-flipped
 * sessionStorage value on a non-admin browser.
 */
const DemoModeBanner = () => {
  const { active, setActive } = useDemoMode();
  const { user } = useAuth();
  if (!active || !user?.is_admin) return null;
  return (
    <div
      className="fixed top-0 left-0 right-0 z-[100] bg-gradient-to-r from-[#5D4050] via-[#9B6A6C] to-[#5D4050] text-white px-4 py-1.5 flex items-center justify-center gap-2 shadow-lg"
      style={{ paddingTop: "max(env(safe-area-inset-top), 6px)" }}
      data-testid="demo-mode-banner"
    >
      <PlayCircle className="h-3.5 w-3.5 text-[#FFE6D7] shrink-0" />
      <span className="text-[11px] tracking-wide font-medium">
        DEMO MODE — TTS + voice forced on
      </span>
      <button
        type="button"
        onClick={() => setActive(false)}
        aria-label="Exit demo mode"
        className="ml-2 inline-flex items-center gap-1 text-[11px] text-[#FFE6D7] hover:text-white bg-white/10 hover:bg-white/20 rounded-full px-2.5 py-0.5 transition-colors"
        data-testid="demo-mode-banner-exit"
      >
        Exit
        <X className="h-3 w-3" />
      </button>
    </div>
  );
};

export default DemoModeBanner;
